import * as React from "react";
import { Search } from "lucide-react";

import { cn } from "@/lib/utils";

// Client-side table search: filters rows already loaded in the browser
// (on a server-paginated list, that is the current page only). A row matches
// when the query appears in any of its values — ids, addresses, hashes,
// amounts, statuses, nested objects' values (merchant name, asset symbol…).

/** How deep nested objects/arrays are walked for searchable values. */
const MAX_DEPTH = 4;

function collectText(value: unknown, depth: number, out: string[]): void {
  if (value === null || value === undefined) return;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    out.push(String(value));
    return;
  }
  if (depth >= MAX_DEPTH || typeof value !== "object") return;
  const values = Array.isArray(value) ? value : Object.values(value);
  for (const v of values) collectText(v, depth + 1, out);
}

/** Lower-cased searchable text of a row, one value per line. */
export function rowSearchText(row: unknown): string {
  const out: string[] = [];
  collectText(row, 0, out);
  return out.join("\n").toLowerCase();
}

/** The rows matching `query` (all rows when it is blank). Row text is indexed once per `rows` change. */
export function useRowSearch<T>(rows: T[], query: string): T[] {
  const index = React.useMemo(() => rows.map((row) => ({ row, text: rowSearchText(row) })), [rows]);
  const term = query.trim().toLowerCase();
  return React.useMemo(
    () => (term ? index.filter((entry) => entry.text.includes(term)).map((entry) => entry.row) : rows),
    [index, rows, term],
  );
}

export interface TableSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

/** Same look as the merchant app's `SearchInput`, but filters instantly — nothing to debounce client-side. */
export function TableSearch({ value, onChange, placeholder = "Search this page…", className }: TableSearchProps) {
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <input
        type="search"
        aria-label="Search table"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "h-9 w-64 rounded-md border border-input bg-transparent pl-8 pr-2 text-sm",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          className,
        )}
      />
    </div>
  );
}

/** Below this many rows the table shows no search box — it's all on screen anyway. */
const SEARCHABLE_ROWS_MIN = 5;

export interface SearchableRowsProps<T> {
  rows: T[];
  /** Renders the table for the matching rows. */
  children: (rows: T[]) => React.ReactNode;
}

/**
 * A search box above a hand-rolled list-page table, filtering its rows client-side.
 * Tables embedded in detail pages and dialogs render their rows directly, without search.
 */
export function SearchableRows<T>({ rows, children }: SearchableRowsProps<T>) {
  const [query, setQuery] = React.useState("");
  const matching = useRowSearch(rows, query);
  if (rows.length < SEARCHABLE_ROWS_MIN) return <>{children(rows)}</>;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <TableSearch value={query} onChange={setQuery} placeholder="Search…" />
        {query.trim() && (
          <span className="text-xs text-muted-foreground">
            {matching.length} of {rows.length} shown
          </span>
        )}
      </div>
      {matching.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">No rows match "{query.trim()}".</p>
      ) : (
        children(matching)
      )}
    </div>
  );
}
